'use client';

import React, { useEffect } from 'react';
import { useCoinPriceSocket } from '@/hooks/useCoinPriceSocket';
import { formatNumber } from '@/utils/coins/formatNumber';
import { formatTomanPrice, isBubbleSymbol } from '@/utils/coins/bubble';

interface TablePriceCellProps {
    symbol: string;
    initialPriceUsdt: number;
    initialPriceToman: number;
    useUnits?: boolean;
    hideUsdtPrice?: boolean;
    onSocketFlash?: (flashClass: string | null) => void;
    symbolType?: 'crypto' | 'gold' | 'currency';
    /** Extra classes for the toman unit label (e.g. hide it where a column header states the unit) */
    unitClassName?: string;
}

export const TablePriceCell: React.FC<TablePriceCellProps> = ({
    symbol,
    initialPriceUsdt,
    initialPriceToman,
    useUnits = false,
    hideUsdtPrice = false,
    onSocketFlash,
    symbolType = 'crypto',
    unitClassName = '',
}) => {
    const { livePriceToman, livePriceUsdt, priceTomanChangeClass, priceUsdtChangeClass } =
        useCoinPriceSocket(symbol, initialPriceUsdt, initialPriceToman, symbolType);

    const activeFlash = priceTomanChangeClass || priceUsdtChangeClass;

    useEffect(() => {
        if (onSocketFlash) {
            onSocketFlash(activeFlash);
        }
    }, [activeFlash, onSocketFlash]);

    function formatNumberWithUnits(amount: number): string {
        if (amount >= 1_000_000_000_000) {
            return (amount / 1_000_000_000_000).toFixed(2) + ' تریلیون';
        } else if (amount >= 1_000_000_000) {
            return (amount / 1_000_000_000).toFixed(2) + ' میلیارد';
        } else if (amount >= 1_000_000) {
            return (amount / 1_000_000).toFixed(2) + ' میلیون';
        } else if (amount >= 1_000) {
            return (amount / 1_000).toFixed(2) + ' هزار';
        } else {
            return amount.toFixed(0);
        }
    }

    return (
        <div className={'flex flex-col justify-start'}>
            {!hideUsdtPrice ? (
                <>
                    <div
                        className={`text-right font-normal font-yekanBakh transition-all duration-700 ${priceUsdtChangeClass ?? ''} text-xs lg:text-sm`}
                        dir={'ltr'}
                    >
                        ${formatNumber(livePriceUsdt, false, true, 'USD', true)}
                    </div>
                    <div
                        className={`text-[10px] lg:text-xs text-nowrap transition-colors duration-700 ${
                            priceTomanChangeClass ?? 'text-primary/60 dark:text-white/70'
                        } inline-flex gap-1`}
                    >
                        {useUnits
                            ? formatNumberWithUnits(livePriceToman)
                            : formatNumber(livePriceToman, false, true, 'TMN', true)}
                        <span>تومان</span>
                    </div>
                </>
            ) : (
                <div
                    className={`text-right font-normal font-yekanBakh transition-all duration-700 ${
                        priceTomanChangeClass ?? ''
                    } inline-flex items-center gap-1 text-xs lg:text-sm whitespace-nowrap`}
                >
                    {isBubbleSymbol(symbol) ? (
                        <bdi dir='ltr'>{formatTomanPrice(livePriceToman, symbol)}</bdi>
                    ) : useUnits ? (
                        formatNumberWithUnits(livePriceToman)
                    ) : (
                        formatNumber(livePriceToman, false, true, 'TMN', true)
                    )}
                    <span
                        className={`text-[10px] lg:text-xs font-normal text-primary/60 dark:text-white/70 ${unitClassName}`}
                    >
                        تومان
                    </span>
                </div>
            )}
        </div>
    );
};
